/**
 * Text Template Schema
 *
 * This module defines the schema for Handlebars-based text templates used
 * in the Text Chart component. It serves as the single source of truth for:
 * - AI prompt generation (backend)
 * - Template validation (frontend)
 * - Documentation
 */

// ============================================================================
// Format Types (Canonical Source)
// ============================================================================

/**
 * Format types for numeric value formatting.
 * This is the canonical source - replots re-exports this for backwards compatibility.
 */
export enum FormatType {
  PERCENTAGE = '%',
  CURRENCY = '$',
  CURRENCY_INTEGER = '$.0f',
  INTEGER = ',',
  DECIMAL = '.',
  SCIENTIFIC = 'e',
  SI = 's',
  YEAR = 'yr',
  DAY = 'day',
  CALENDAR_YEAR = 'yyyy',
  PERCENTAGE_POINT = 'pp',
}

export type Format = FormatType;

/**
 * Available format types for the FORMAT helper.
 * Derived from the FormatType enum for use in template schema.
 */
export const TEXT_TEMPLATE_FORMAT_TYPES = {
  PERCENTAGE: FormatType.PERCENTAGE,
  CURRENCY: FormatType.CURRENCY,
  CURRENCY_INTEGER: FormatType.CURRENCY_INTEGER,
  INTEGER: FormatType.INTEGER,
  DECIMAL: FormatType.DECIMAL,
  SCIENTIFIC: FormatType.SCIENTIFIC,
  SI: FormatType.SI,
  YEAR: FormatType.YEAR,
  DAY: FormatType.DAY,
  CALENDAR_YEAR: FormatType.CALENDAR_YEAR,
  PERCENTAGE_POINT: FormatType.PERCENTAGE_POINT,
} as const;

export type TextTemplateFormatKey = keyof typeof TEXT_TEMPLATE_FORMAT_TYPES;

export const TEMPLATE_SPECIAL_FORMAT_KEYS = ['ORDINAL', 'DATE'] as const;

export type TemplateSpecialFormatKey =
  (typeof TEMPLATE_SPECIAL_FORMAT_KEYS)[number];

// ============================================================================
// Aggregation Types
// ============================================================================

export const AGGREGATION_TYPES = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT'] as const;

export type AggregationType = (typeof AGGREGATION_TYPES)[number];

// ============================================================================
// Metric reference pattern (single source of truth for template parsing)
// ============================================================================

/**
 * Matches metric references in Handlebars helpers (SUM "x", AVG "y", VALUE "z", etc.).
 * Capture group 2 is the metric name. Reset lastIndex before each use when using with exec().
 */
export const METRIC_REF_PATTERN =
  /\b(SUM|AVG|MEAN|MIN|MAX|COUNT|VALUE|HAS_DATA|ARG_MAX|ARG_MIN|RANK|MAX_POINT|MIN_POINT|PERCENTILE_RANK)\s+["']([a-z0-9_]+)["']/g;

// ============================================================================
// Comparison Operators
// ============================================================================

export const COMPARISON_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'eq'] as const;

// ============================================================================
// Arithmetic Operators
// ============================================================================

export const ARITHMETIC_OPERATORS = [
  'add',
  'subtract',
  'multiply',
  'divide',
] as const;

export type ArithmeticOperator = (typeof ARITHMETIC_OPERATORS)[number];

// ============================================================================
// Helper Definitions
// ============================================================================

export interface HelperDefinition {
  name: string;
  syntax: string;
  description: string;
  examples: string[];
  notes?: string[];
}

const AGGREGATION_HELPER_DEFINITIONS: HelperDefinition[] =
  AGGREGATION_TYPES.map((type) => ({
    name: type,
    syntax: `{{${type} "metric_name"}}`,
    description: `Computes the ${type.toLowerCase()} of all values for the specified metric.`,
    examples: [
      `{{${type} "headcount"}}`,
      `{{${type} "metric"}}  (uses currently selected metric)`,
    ],
    notes: [
      'metric_name must be lowercase letters and underscores only (e.g., headcount, attrition_rate)',
      'Use "metric" to reference the currently selected metric in the dashboard',
    ],
  }));

const FORMAT_HELPER_DEFINITION: HelperDefinition = {
  name: 'FORMAT',
  syntax: '{{FORMAT (AGG "metric_name") format="FORMAT_TYPE"}}',
  description:
    'Formats the result of an aggregation with the specified format type.',
  examples: [
    '{{FORMAT (SUM "headcount") format="SI"}}',
    '{{FORMAT (AVG "salary") format="$"}}',
    '{{FORMAT (SUM "growth_rate") format="%"}}',
    '{{FORMAT (RANK "overall" (filter "COMPANY")) format="ORDINAL"}}',
    '{{FORMAT (MAX_POINT "overall" (filter "COMPANY") property="date") format="DATE"}}',
  ],
  notes: [
    `Available format types: ${Object.entries(TEXT_TEMPLATE_FORMAT_TYPES)
      .map(([key, val]) => `${key} ("${val}")`)
      .join(', ')}`,
    `Template-only formats: ${TEMPLATE_SPECIAL_FORMAT_KEYS.join(', ')} (ORDINAL: 1st/2nd/3rd; DATE: e.g. 2021-03 -> March 2021)`,
    'If format is omitted, defaults to SI notation',
  ],
};

const FILTER_HELPER_DEFINITION: HelperDefinition = {
  name: 'filter',
  syntax: '{{filter "filter_id"}}',
  description:
    'Returns the current value of a dashboard filter as a formatted string.',
  examples: [
    '{{filter "COMPANY"}}',
    '{{filter "REGION"}}',
    '{{filter "DATE_RANGE"}}',
  ],
  notes: [
    'filter_id should match the ID of a filter defined in the dashboard',
    'Returns comma-separated values for multi-select filters',
    'Returns "min - max" format for range filters',
  ],
};

const COMPARISON_HELPER_DEFINITIONS: HelperDefinition[] = [
  {
    name: 'gt',
    syntax: '(gt a b)',
    description: 'Returns true if a > b (greater than)',
    examples: ['{{#if (gt (SUM "headcount") 1000)}}Large team{{/if}}'],
  },
  {
    name: 'gte',
    syntax: '(gte a b)',
    description: 'Returns true if a >= b (greater than or equal)',
    examples: ['{{#if (gte (AVG "tenure") 5)}}Experienced{{/if}}'],
  },
  {
    name: 'lt',
    syntax: '(lt a b)',
    description: 'Returns true if a < b (less than)',
    examples: ['{{#if (lt (SUM "attrition_rate") 10)}}Low attrition{{/if}}'],
  },
  {
    name: 'lte',
    syntax: '(lte a b)',
    description: 'Returns true if a <= b (less than or equal)',
    examples: ['{{#if (lte (COUNT "employees") 50)}}Small company{{/if}}'],
  },
  {
    name: 'eq',
    syntax: '(eq a b)',
    description: 'Returns true if a === b (strict equality)',
    examples: [
      '{{#if (eq (filter "REGION") "US")}}United States{{/if}}',
      '{{#if (eq (COUNT "metric") 0)}}No data{{/if}}',
    ],
  },
];

const ARITHMETIC_HELPER_DEFINITIONS: HelperDefinition[] = [
  {
    name: 'add',
    syntax: '{{add value1 value2 ...}}',
    description: 'Adds two or more numeric values together.',
    examples: [
      '{{add (SUM "salary") (SUM "bonus")}}',
      '{{add (AVG "q1") (AVG "q2") (AVG "q3") (AVG "q4")}}',
      '{{FORMAT (add (SUM "base_pay") (SUM "equity")) format="$"}}',
    ],
    notes: [
      'Accepts any number of arguments (minimum 2)',
      'Non-numeric values are filtered out',
      'Returns empty string if no valid numeric values',
    ],
  },
  {
    name: 'subtract',
    syntax: '{{subtract a b}}',
    description: 'Subtracts b from a.',
    examples: [
      '{{subtract (SUM "current_year") (SUM "previous_year")}}',
      '{{subtract (MAX "revenue") (MIN "revenue")}}',
      '{{FORMAT (subtract (COUNT "total") (COUNT "excluded")) format=","}}',
    ],
    notes: [
      'Takes exactly 2 arguments',
      'Returns empty string if either value is not numeric',
    ],
  },
  {
    name: 'multiply',
    syntax: '{{multiply value1 value2 ...}}',
    description: 'Multiplies two or more numeric values together.',
    examples: [
      '{{multiply (AVG "price") (SUM "quantity")}}',
      '{{multiply (AVG "rating") 100}}',
      '{{FORMAT (multiply (AVG "score") 5) format="."}}',
    ],
    notes: [
      'Accepts any number of arguments (minimum 2)',
      'Non-numeric values are filtered out',
      'Returns empty string if no valid numeric values',
    ],
  },
  {
    name: 'divide',
    syntax: '{{divide a b}}',
    description: 'Divides a by b.',
    examples: [
      '{{divide (SUM "revenue") (COUNT "employees")}}',
      '{{divide (SUM "wins") (SUM "total_games")}}',
      '{{FORMAT (divide (SUM "profit") (SUM "revenue")) format="%"}}',
    ],
    notes: [
      'Takes exactly 2 arguments',
      'Returns empty string if either value is not numeric',
      'Returns empty string if dividing by zero',
    ],
  },
];

// ============================================================================
// Conditional Syntax
// ============================================================================

export interface ConditionalDefinition {
  name: string;
  syntax: string;
  description: string;
  examples: string[];
}

const CONDITIONAL_DEFINITIONS: ConditionalDefinition[] = [
  {
    name: 'if',
    syntax: '{{#if condition}}...{{/if}}',
    description: 'Renders content if the condition is truthy.',
    examples: [
      '{{#if (gt (SUM "headcount") 100)}}Large organization{{/if}}',
      '{{#if (filter "COMPANY")}}Filtered by: {{filter "COMPANY"}}{{/if}}',
    ],
  },
  {
    name: 'if-else',
    syntax: '{{#if condition}}...{{else}}...{{/if}}',
    description:
      'Renders first block if condition is truthy, otherwise renders the else block.',
    examples: [
      '{{#if (gt (SUM "growth") 0)}}Growing{{else}}Declining{{/if}}',
      '{{#if (eq (filter "REGION") "US")}}Domestic{{else}}International{{/if}}',
    ],
  },
];

// ============================================================================
// Complete Schema
// ============================================================================

interface TextTemplateSchema {
  formatTypes: typeof TEXT_TEMPLATE_FORMAT_TYPES;
  templateSpecialFormatKeys: typeof TEMPLATE_SPECIAL_FORMAT_KEYS;
  aggregationTypes: typeof AGGREGATION_TYPES;
  comparisonOperators: typeof COMPARISON_OPERATORS;
  arithmeticOperators: typeof ARITHMETIC_OPERATORS;
  helpers: {
    aggregation: HelperDefinition[];
    format: HelperDefinition;
    filter: HelperDefinition;
    comparison: HelperDefinition[];
    arithmetic: HelperDefinition[];
  };
  conditionals: ConditionalDefinition[];
}

export const TEXT_TEMPLATE_SCHEMA: TextTemplateSchema = {
  formatTypes: TEXT_TEMPLATE_FORMAT_TYPES,
  templateSpecialFormatKeys: TEMPLATE_SPECIAL_FORMAT_KEYS,
  aggregationTypes: AGGREGATION_TYPES,
  comparisonOperators: COMPARISON_OPERATORS,
  arithmeticOperators: ARITHMETIC_OPERATORS,
  helpers: {
    aggregation: AGGREGATION_HELPER_DEFINITIONS,
    format: FORMAT_HELPER_DEFINITION,
    filter: FILTER_HELPER_DEFINITION,
    comparison: COMPARISON_HELPER_DEFINITIONS,
    arithmetic: ARITHMETIC_HELPER_DEFINITIONS,
  },
  conditionals: CONDITIONAL_DEFINITIONS,
};
